import { useCallback, useEffect, useState } from 'react';
import { Box, Button, IconButton, Input, Skeleton, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import '@fontsource/rubik';

import AuthLayout from '../layouts/AuthLayout';
import Header from '../userfiles/components/Header';
import useDebounce from '../dashboard/hooks/useDebounce';
import useProductAccessActions from './useProductAccessActions';
import UserAccessRow, { gridColumnsFor } from './components/UserAccessRow';
import EnableOrgProductDialog from './components/EnableOrgProductDialog';

const SKELETON_COUNT = 3;

/**
 * Product Access — Admin-only page that grants Story Builder or SmartHub to a
 * customer user, replacing hand-edited MySQL.
 *
 * Access is two booleans (the organisation gate and the user's own row) but one
 * switch: the switch drives the user row, and granting through a closed gate is
 * confirmed in a dialog because it opens the product for the whole company.
 *
 * Revoke ships a plain success snackbar rather than an undo (design §14): it
 * affects one person, is reversible by clicking back on, and the undo component
 * it would otherwise match exists only on `develop`.
 */
const ProductAccess = () => {
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const [confirmRequest, setConfirmRequest] = useState(null);

  const { searchUsers, setProductAccess, users, isLoading, isError, hasLoaded, pendingKey } =
    useProductAccessActions();

  useEffect(() => {
    searchUsers({ query: debouncedSearch });
  }, [debouncedSearch, searchUsers]);

  const runUpdate = useCallback(
    async ({ user, product, access }) => {
      const result = await setProductAccess({ userId: user.id, productKey: product.key, access });
      // A gate flip is organisation-wide, so every other visible row of that
      // company is now stale — refetch the whole result list.
      if (result?.success && result.orgGateEnabled) {
        searchUsers({ query: debouncedSearch });
      }
      return result;
    },
    [setProductAccess, searchUsers, debouncedSearch]
  );

  const onToggle = useCallback(
    (user, product, nextChecked) => {
      // Granting while the organisation gate is closed is the only action with a
      // blast radius beyond this person — ask first, and send nothing until then.
      if (nextChecked && !product.org_enabled) {
        setConfirmRequest({ user, product });
        return;
      }
      runUpdate({ user, product, access: nextChecked });
    },
    [runUpdate]
  );

  const onConfirmEnable = useCallback(async () => {
    if (!confirmRequest) return;
    const { user, product } = confirmRequest;
    await runUpdate({ user, product, access: true });
    setConfirmRequest(null);
  }, [confirmRequest, runUpdate]);

  // Every row carries the whole catalog in catalog order, so the first row is
  // enough to build the column headers.
  const catalog = users[0]?.products || [];
  const showIdle = !debouncedSearch.trim();
  const showResults = !showIdle && !isLoading && !isError && users.length > 0;
  const showNoMatches = !showIdle && !isLoading && !isError && hasLoaded && users.length === 0;
  const confirmPending =
    confirmRequest !== null && pendingKey === `${confirmRequest.user.id}:${confirmRequest.product.key}`;

  return (
    <AuthLayout sx={{ padding: '2.1% 3.4% 2.1% 3.4%' }}>
      <Header />
      <Box px={{ xs: 1.5, sm: 2, md: 3 }} pr={{ xs: 1.5, sm: 3, md: 6 }} className="flex col" mt={4}>
        <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', color: 'black', mb: 2 }} gap={1}>
          <KeyboardBackspaceOutlinedIcon onClick={() => navigate(-1)} sx={{ color: 'black', cursor: 'pointer' }} />
          <Typography
            sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '11px', md: '12px', lg: '14px', xl: '20px' } }}
          >
            Back
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, md: 4 },
            width: '100%',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockOpenOutlinedIcon
              sx={{ fontSize: { xs: '18px', md: '22px', lg: '26px', xl: '36px' }, color: '#5A6474' }}
            />
            <Box>
              <Typography
                sx={{
                  color: '#000000',
                  fontFamily: 'Rubik',
                  fontWeight: 600,
                  fontSize: { xs: '16px', md: '18px', lg: '20px', xl: '30px' },
                }}
              >
                Product Access
              </Typography>
              <Typography
                sx={{ color: '#000000', fontFamily: 'Rubik', fontSize: { xs: '11px', md: '12px', lg: '13px', xl: '20px' } }}
              >
                Give a customer access to Story Builder or SmartHub.
              </Typography>
            </Box>
          </Box>

          <Box id="search" sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Input
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              inputProps={{ maxLength: 100, 'aria-label': 'Search by name, email, or company' }}
              sx={{
                width: { xs: '100%', md: '280px', xl: '360px' },
                borderBottom: 2,
                borderColor: '#333333',
                input: {
                  fontSize: { xs: '12px', md: '13px', lg: '14px', xl: '20px' },
                  fontWeight: 400,
                  color: '#000000',
                },
              }}
              placeholder="Search by name, email, or company"
              startAdornment={
                <IconButton
                  sx={{
                    paddingBottom: 1,
                    fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' },
                    '& .MuiSvgIcon-root': { fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' } },
                    paddingRight: 2,
                  }}
                >
                  <SearchIcon style={{ color: '#000000' }} />
                </IconButton>
              }
            />
          </Box>
        </Box>

        {showIdle && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1 }}
          >
            <Typography
              sx={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: { xs: '13px', lg: '15px', xl: '20px' }, color: '#5A6474' }}
            >
              Search for a customer
            </Typography>
            <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '11px', lg: '12px', xl: '16px' }, color: '#8A909C' }}>
              Search by name, email, or company to manage their product access.
            </Typography>
          </Box>
        )}

        {!showIdle && isError && !isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, flexDirection: 'column' }}>
            <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '12px', lg: '13px' }, color: 'error.main' }}>
              Couldn't load people.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => searchUsers({ query: debouncedSearch })}
              sx={{ fontFamily: 'Rubik', textTransform: 'none', fontSize: { xs: '11px', lg: '12px' } }}
            >
              Try again
            </Button>
          </Box>
        )}

        {!showIdle && isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                animation="pulse"
                sx={{ borderRadius: '6px', height: { xs: '96px', md: '86px', xl: '110px' } }}
              />
            ))}
          </Box>
        )}

        {showResults && (
          <>
            <Typography
              sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', lg: '11px', xl: '14px' }, color: '#8A909C', mb: 1 }}
            >
              {users.length} {users.length === 1 ? 'person' : 'people'}
            </Typography>
            {/* One header per product from sm up, so the name is not repeated on
                every row. Below sm the row stacks and carries its own labels. */}
            <Box
              data-testid="product-access-header"
              sx={{
                display: { xs: 'none', sm: 'grid' },
                gridTemplateColumns: gridColumnsFor(catalog.length),
                alignItems: 'center',
                columnGap: { sm: 1, xl: 2 },
                pb: 1,
                borderBottom: '2px solid #EBEBEB',
              }}
            >
              <Box />
              {catalog.map((product) => (
                <Typography
                  key={product.key}
                  sx={{
                    fontFamily: 'Rubik',
                    fontWeight: 800,
                    fontSize: { xs: '10px', md: '13px', lg: '14.67px', xl: '21px' },
                    color: '#08231B',
                  }}
                >
                  {product.label}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {users.map((user) => (
                <UserAccessRow key={user.id} user={user} pendingKey={pendingKey} onToggle={onToggle} />
              ))}
            </Box>
          </>
        )}

        {showNoMatches && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1 }}
          >
            <Typography
              sx={{ fontFamily: 'Rubik', fontWeight: 500, fontSize: { xs: '12px', lg: '13px', xl: '18px' }, color: '#5A6474' }}
            >
              {`No one matches "${debouncedSearch}".`}
            </Typography>
            <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '11px', lg: '12px', xl: '16px' }, color: '#8A909C' }}>
              Check the spelling, or search by company instead.
            </Typography>
          </Box>
        )}
      </Box>

      <EnableOrgProductDialog
        open={Boolean(confirmRequest)}
        request={confirmRequest}
        isSubmitting={confirmPending}
        onClose={() => setConfirmRequest(null)}
        onConfirm={onConfirmEnable}
      />
    </AuthLayout>
  );
};

export default ProductAccess;
